import type { Importacion, ResultadoLinea } from '../../../api/ops/clientesImportar';
import type { EstadoChip } from '../../../componentes/Chip';
import type { NombreIcono } from '../../../componentes/iconos';
import type { TarjetaAyuda } from '../../../marco/cabecera';

/**
 * Textos de «Importar clientes» (2.6), tal cual del diseño (`2.6-importar-clientes.html`) salvo donde una decisión lo
 * corrige: N4 literal con «De dónde viene el permiso y cuándo» y «Eres responsable de conservar esa prueba…» (G202), el
 * matiz de la lista de supresión (G197: es de este negocio y no impide que la persona vuelva por sí misma) y un solo texto
 * al descartar la vista previa (revisión de la exportación, §7.12).
 */
export const PERMISO_IMPORTAR = 'clientes.importaciones.gestionar';

export const ESTADO: Record<Importacion['status'], [string, EstadoChip]> = {
    validating: ['Comprobando', 'neutral'],
    invalid: ['Archivo no válido', 'problem'],
    preview_ready: ['Sin confirmar', 'pending'],
    importing: ['Importando', 'neutral'],
    completed: ['Importada', 'confirmed'],
    failed: ['Ha fallado', 'problem'],
    cancelled: ['Descartada', 'neutral'],
};

export const PASOS = ['Archivo', 'Permisos', 'Revisar'] as const;

/** Líneas que conviene mirar, en este orden (sin cifras en el filtro, G140). */
export const FILTROS_LINEAS: readonly { resultado: ResultadoLinea; etiqueta: string; icono: NombreIcono; nota: string }[] = [
    { resultado: 'error', etiqueta: 'Con errores', icono: 'circle-alert', nota: 'Las líneas con errores no se importan. Corrígelas y vuelve a subir el archivo, o importa sin ellas.' },
    { resultado: 'doubtful', etiqueta: 'Dudosas', icono: 'git-compare', nota: 'Las dudosas se importan como clientes nuevos y aparecen en Duplicados para que decidas.' },
    { resultado: 'merged', etiqueta: 'Unidas', icono: 'git-merge', nota: 'Mismo teléfono = misma persona: se guarda un solo cliente con los datos de las dos líneas.' },
    // G197: la lista de supresión es de este negocio y no impide que la persona vuelva por sí misma.
    { resultado: 'suppressed', etiqueta: 'Suprimidas', icono: 'shield-off', nota: 'Pidieron que tu negocio borrara sus datos: no se importan. Si vuelven por sí mismos, pueden darte un permiso nuevo.' },
];

export const NOTA_ERRORES_HECHA = 'Estas líneas no se importaron. Corrígelas en tu archivo y súbelo otra vez: lo ya importado no se duplica.';

/** Lo que dice una línea sin mensaje de la API (los ejemplos del diseño). */
export const LINEA_POR_DEFECTO: Partial<Record<ResultadoLinea, string>> = {
    doubtful: 'Se importa y queda como sugerencia en Duplicados.',
    merged: 'Mismo teléfono que otra línea: se juntan en un solo cliente.',
    suppressed: 'Esta persona pidió que se borraran sus datos. No se importa.',
};

export const AYUDA: Record<'lista' | 'archivo' | 'permisos' | 'revisar' | 'ver', TarjetaAyuda[]> = {
    lista: [
        { icono: 'file-spreadsheet', titulo: 'Importar', texto: 'Sube un CSV con la plantilla. Antes de importar nada ves qué pasará con cada línea: nuevos, actualizados, dudosos y errores.' },
        { icono: 'git-merge', titulo: 'Sin duplicados', texto: 'Mismo teléfono = misma persona: se juntan y se completan los datos vacíos sin pisar los que ya tienes. Lo dudoso queda en Duplicados.' },
        { icono: 'shield-check', titulo: 'Permisos', texto: 'Sin prueba de que aceptaron mensajes comerciales, solo reciben confirmaciones, pases y recordatorios.' },
    ],
    archivo: [
        { icono: 'file-spreadsheet', titulo: 'La plantilla', texto: 'Los teléfonos sin prefijo toman el de tu país. Los emails inventados se descartan.' },
        { icono: 'upload', titulo: 'Subir', texto: 'Elige el archivo desde tu ordenador, tu iPad (Archivos) o tu móvil. Hasta 20 MB.' },
    ],
    // N4 (G202): la ayuda literal.
    permisos: [{ icono: 'shield-check', titulo: 'Permiso comercial', texto: 'Solo cuenta si cada cliente lo aceptó de forma expresa y puedes demostrarlo. Si dudas, elige «No».' }],
    revisar: [
        { icono: 'list-checks', titulo: 'Vista previa', texto: 'Todavía no se ha importado nada. Las líneas con errores no se importan; corrígelas y vuelve a subir el archivo si las quieres.' },
        { icono: 'x', titulo: 'Descartar', texto: 'Si no la quieres, descártala: no se importa nada. Cambiar los permisos también la descarta y sube el archivo otra vez.' },
        { icono: 'refresh-cw', titulo: 'Repetir', texto: 'Puedes subir el mismo archivo otra vez: no crea duplicados.' },
    ],
    ver: [{ icono: 'history', titulo: 'Importación', texto: 'El resumen de lo que se importó y las líneas que conviene mirar.' }],
};

/** N4 literal (G202). */
export const N4 = {
    pregunta: '¿Tienes prueba de que estos clientes aceptaron recibir mensajes comerciales?',
    ejemplo: 'Por ejemplo, una casilla marcada en tu web o un formulario firmado. Te la pueden pedir.',
    si: 'Sí, tengo la prueba',
    siTexto: 'Las líneas que marcan permiso comercial lo reciben, con origen «importación» y la fecha de tu archivo.',
    origen: 'De dónde viene el permiso y cuándo',
    origenEjemplo: 'Por ejemplo, «Casilla en mi web, 2024».',
    responsable: 'Eres responsable de conservar esa prueba y de mostrarla si te la piden.',
    no: 'No, o no estoy seguro',
    noTexto: 'Todos reciben solo mensajes de servicio: confirmaciones, pases y recordatorios. Nunca campañas.',
} as const;

export const TEXTOS = {
    vacia: 'Todavía no has importado clientes.',
    vaciaTexto: 'Descarga la plantilla, rellénala con los clientes de tu otro sistema y súbela. Antes de importar nada verás qué va a pasar con cada línea.',
    soloLectura: 'El negocio está en solo lectura: puedes ver tus importaciones, pero no importar ni confirmar.',
    plantillaTitulo: 'Usa la plantilla de Reservaria',
    plantillaTexto: 'Una línea por cliente: nombre, teléfono con prefijo, email, Instagram, idioma, etiquetas y permisos por canal, y una columna por cada campo propio de tu negocio.',
    elegir: 'Elige el archivo CSV',
    elegirAyuda: 'Toca para elegirlo o arrástralo aquí. Hasta 20 MB, con la plantilla de Reservaria.',
    elegirAyudaMovil: 'Toca para elegirlo en tu móvil o en Archivos. Hasta 20 MB.',
    noCsv: 'Tiene que ser un archivo .csv. Guarda tu hoja de cálculo como CSV.',
    grande: 'Pesa más de 20 MB. Divide el archivo en dos.',
    faltaArchivo: 'Elige un archivo CSV para seguir.',
    servicioSiempre: 'Las confirmaciones, los pases y los recordatorios se envían siempre: son mensajes de servicio.',
    comprobando: 'Comprobando el archivo…',
    comprobandoTexto: 'Normalizo los teléfonos con el prefijo de tu país, descarto los emails inventados y busco duplicados. No se importa nada todavía.',
    importandoTexto: 'Puedes cerrar el panel: la importación sigue y la verás terminada en la lista.',
    noValido: 'El archivo no sigue la plantilla',
    noValidoTexto: 'No se ha importado nada.',
    descartada: 'Se descartó la vista previa: no se importó nada. Si quieres estos clientes, sube el archivo otra vez.',
    haFallado: 'La importación ha fallado. Lo ya importado no se duplica si la repites: vuelve a subir el archivo.',
    cambiarNota: 'Cambiarlo descarta esta vista previa y vuelve a subir el archivo.',
    lineasTitulo: 'Líneas que tienes que mirar',
    sinLineas: 'No hay líneas de este tipo.',
    // Un solo texto al descartar (§7.12 de la revisión): el de la ventana.
    descartarTitulo: '¿Descartar esta vista previa?',
    descartarTexto: 'No se importa nada. La vista previa queda como «Descartada» en la lista y sus líneas se borran. Para importar estos clientes, sube el archivo otra vez.',
    descartadaHecho: 'Vista previa descartada. No se ha importado nada.',
    conPrueba: 'Con prueba de permiso comercial',
    conPruebaTexto: 'Quien lo marca en el archivo podrá recibir campañas por ese canal.',
    soloServicio: 'Solo mensajes de servicio',
    soloServicioTexto: 'Recibirán confirmaciones, pases y recordatorios, pero no campañas.',
} as const;

/** «41 para importar · 3 con errores · 1 dudoso» (las cifras del resumen de la importación, `Import.summary`). */
export function lineaResumen(i: Importacion): string {
    if (i.status === 'invalid') {
        return 'No sigue la plantilla: no se importó nada';
    }
    if (i.status === 'cancelled') {
        return 'Vista previa descartada: no se importó nada';
    }
    if (i.status === 'validating') {
        return 'Comprobando el archivo…';
    }
    const s = i.summary ?? {};
    const total = totalImportable(i);
    return [
        `${total.toLocaleString('es-ES')} ${i.status === 'completed' ? 'importados' : 'para importar'}`,
        (s.errors ?? 0) > 0 ? `${String(s.errors)} con errores` : '',
        (s.doubtful ?? 0) > 0 ? `${String(s.doubtful)} ${s.doubtful === 1 ? 'dudoso' : 'dudosos'}` : '',
    ]
        .filter((t) => t !== '')
        .join(' · ');
}

export function totalImportable(i: Pick<Importacion, 'summary'>): number {
    const s = i.summary ?? {};
    return (s.new ?? 0) + (s.updated ?? 0) + (s.merged ?? 0) + (s.doubtful ?? 0);
}
